import _ from 'lodash';

import { daysFromNow } from '@zapier/date';
import { buildAccountSwitcherUrl } from '@zapier/identity';
import {
  EntityActions,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
  entityStatuses,
  entityUtils,
  getLocalStorage,
  setLocalStorage,
} from '@zapier/toolbox-redux-framework';
import { patchJson, sendFormData } from '@zapier/toolbox-browser-fetch';
import {
  isFullStoryInstalled,
  setFullStoryUserVars,
} from '@zapier/toolbox-tracking';
import { infinitize, orify } from '@zapier/common-utils';

import * as Profile from './Profile';
import * as ToastNotification from './ToastNotification';
import { getSwitchesFromState } from '../utils/SwitchesUtils';
import partialWithDefault from '../utils/partialWithDefault';

export const type = 'account';

export const COMPANY_NAME = 'company_name';
export const COMPANY_ADDRESS = 'company_address';
export const SEND_INVOICE_EMAIL = 'send_invoice_email';
export const INVOICE_EMAIL = 'invoice_email';
export const NAME = 'name';
export const LOGO_URL = 'logo_url';
export const LOGO_CHOICE = 'logo_choice';
export const IS_TRIALING_TEAMS = 'is_trialing_teams';
export const TEAMS_TRIAL_AUTO_UPGRADE = 'teams_trial_auto_upgrade';
export const IS_TRIALING_COMPANIES = 'is_trialing_companies';

const DID_EXTEND_TRIAL = 'didExtendTrial';

export const api = buildEntityApi(type, '/api/v3/accounts', accountApi => {
  const { loadCollection } = accountApi;

  accountApi.loadCollection = (...args) =>
    loadCollection(...args).catch(
      _.partial(entityUtils.remove401StatusFromGetErrorResponse, type)
    );

  accountApi.uploadImage = (id, { file }) => {
    const data = new FormData();
    data.append('id', id);
    data.append('file', file);
    return sendFormData(accountApi.modelEndpoint(`/${id}/logo`), data);
  };

  accountApi.extendTrial = (id, { reason, shouldAutoUpgrade = false }) => {
    return patchJson(accountApi.modelEndpoint(`/${id}/trial`), {
      auto_upgrade: shouldAutoUpgrade,
      reason,
    });
  };
});

const IS_AUTOREPLAY_ENABLED = 'autoreplay_errored_tasks_override';
const CURRENT_ACCOUNT_ID = 'currentAccountId';

export const selectors = buildEntitySelectors(type, sel => {
  sel.isActive = (state, entity) =>
    sel.id(entity) === sel.all.currentAccountId(state);
  sel.logoStatus = _.partial(sel.stateItem, 'logoStatus');
  sel.surveysSubmitted = partialWithDefault(
    sel.dataItem,
    ['surveys_submitted'],
    {}
  );
  sel.companyAccountEndDate = partialWithDefault(
    sel.dataItem,
    ['company_account_end_date'],
    null
  );
  sel.companyTrialEndDate = partialWithDefault(
    sel.dataItem,
    ['companies_trial_end_date'],
    null
  );
  sel.teamTrialEndDate = partialWithDefault(
    sel.dataItem,
    ['teams_trial_end_date'],
    null
  );
  sel.teamAccountEndDate = partialWithDefault(
    sel.dataItem,
    ['team_account_end_date'],
    null
  );
  sel.isInExpertsMvp = partialWithDefault(
    sel.dataItem,
    ['is_in_experts_mvp'],
    false
  );
  sel.isTeam = partialWithDefault(sel.dataItem, ['is_team_account'], false);
  sel.isCompany = partialWithDefault(
    sel.dataItem,
    ['is_company_account'],
    false
  );
  sel.wasTeam = entity => {
    return !sel.isTeam(entity) && !!sel.teamAccountEndDate(entity);
  };
  sel.wasCompany = entity => {
    return !sel.isCompany(entity) && !!sel.companyAccountEndDate(entity);
  };
  sel.isOrWasTeam = orify(sel.isTeam, sel.wasTeam);
  sel.isOrWasCompany = orify(sel.isCompany, sel.wasCompany);
  sel.sendInvoiceEmail = _.partial(sel.dataItem, SEND_INVOICE_EMAIL);
  sel.invoiceEmail = _.partial(sel.dataItem, INVOICE_EMAIL);
  sel.companyName = _.partial(sel.dataItem, COMPANY_NAME);
  sel.companyAddress = _.partial(sel.dataItem, COMPANY_ADDRESS);
  sel.logoUrl = _.partial(sel.dataItem, LOGO_URL);
  sel.logoChoice = _.partial(sel.dataItem, LOGO_CHOICE);
  sel.limits = _.partial(sel.dataItem, 'limits');
  sel.name = _.partial(sel.dataItem, NAME);
  sel.nameOrLoading = entity => sel.name(entity) || Profile.LOADING;
  sel.ownerEmail = _.partial(sel.dataItem, 'owner.email');
  sel.ownerId = _.partial(sel.dataItem, 'owner.id');
  sel.ownerName = _.partial(sel.dataItem, 'owner.name');
  sel.numTasks = _.partial(sel.dataItem, 'limits.allotment.tasks.count');
  sel.numTasksMax = _.flow(
    _.partial(sel.dataItem, 'limits.allotment.tasks.limit'),
    infinitize
  );
  sel.numLiveZaps = _.partial(sel.dataItem, 'limits.allotment.zaps.count');
  sel.numLiveZapsMax = _.flow(
    _.partial(sel.dataItem, 'limits.allotment.zaps.limit'),
    infinitize
  );
  sel.firstKnownUpgradeDate = _.partial(
    sel.dataItem,
    'first_known_upgrade_date'
  );
  sel.isAutoreplayEnabled = _.partial(sel.dataItem, IS_AUTOREPLAY_ENABLED);
  sel.isUnlimited = _.partial(sel.dataItem, 'is_unlimited');
  sel.isPaid = _.partial(sel.dataItem, 'is_paid');
  sel.isSamlRequired = _.partial(sel.dataItem, 'is_saml_required');
  sel.isControlledByIdentityProvider = _.partial(
    sel.dataItem,
    'is_controlled_by_identity_provider'
  );
  sel.isTrialing = _.partial(sel.dataItem, 'is_trialing');
  sel.isTrialingCompanies = partialWithDefault(
    sel.dataItem,
    ['is_trialing_companies'],
    false
  );
  sel.isTrialingTeams = partialWithDefault(
    sel.dataItem,
    ['is_trialing_teams'],
    false
  );
  sel.trialEndDate = partialWithDefault(sel.dataItem, ['trial_end_date'], null);
  sel.trialDaysLeft = entity => {
    return daysFromNow(sel.trialEndDate(entity));
  };
  sel.companiesTrialEndDate = partialWithDefault(
    sel.dataItem,
    ['companies_trial_end_date'],
    null
  );
  sel.companiesAccountEndDate = partialWithDefault(
    sel.dataItem,
    ['companies_account_end_date'],
    null
  );
  sel.teamsTrialEndDate = partialWithDefault(
    sel.dataItem,
    ['teams_trial_end_date'],
    null
  );
  sel.companiesTrialDaysLeft = entity => {
    return daysFromNow(sel.companiesTrialEndDate(entity));
  };
  sel.hasBeenInCompaniesTrial = partialWithDefault(
    sel.dataItem,
    ['has_been_in_companies_trial'],
    false
  );
  sel.teamsTrialDaysLeft = entity => {
    return daysFromNow(sel.teamsTrialEndDate(entity));
  };
  sel.canExtendTrialForPaths = partialWithDefault(
    sel.dataItem,
    ['can_extend_trial_for_paths'],
    false
  );
  sel.canExtendTrialForEmailSupport = partialWithDefault(
    sel.dataItem,
    ['can_extend_trial_for_email_support'],
    false
  );
  sel.canUsePaths = partialWithDefault(
    sel.dataItem,
    ['allow_branching_zaps'],
    false
  );
  sel.trialExtensions = partialWithDefault(
    sel.dataItem,
    ['enabled_trial_extensions'],
    []
  );
  sel.hasExtendedTrialFor = (extension, entity) => {
    return _.includes(sel.trialExtensions(entity), extension);
  };
  sel.didExtendTrial = partialWithDefault(
    sel.stateItem,
    [DID_EXTEND_TRIAL],
    false
  );
  sel.isChatEligible = partialWithDefault(
    sel.dataItem,
    ['is_chat_eligible'],
    false
  );
  sel.isFree = entity =>
    !sel.isUnlimited(entity) && !sel.isTrialing(entity) && !sel.isPaid(entity);
  sel.isSwitchOn = (switchName, entity) =>
    sel.dataItem(`switches.${switchName}`, entity) === true;
  sel.planId = _.partial(sel.dataItem, 'plan_id');
  sel.planGroupId = _.partial(sel.dataItem, 'plan_group_id');
  sel.customerId = _.partial(sel.dataItem, 'customer_id');
  sel.planName = _.partial(sel.dataItem, 'plan.name');
  sel.hasCompanies = _.partial(sel.dataItem, 'plan.allow_companies');
  sel.isPlanMetered = _.partial(sel.dataItem, 'plan.metered_task_pricing');
  sel.prettyPlanName = entity =>
    `${sel.isTeam(entity) ? 'Team' : sel.planName(entity)} Plan`;
  sel.periodEndDate = _.partial(sel.dataItem, 'period_end');
  sel.periodStartDate = _.partial(sel.dataItem, 'period_start');
  sel.lastChargeFail = _.partial(sel.dataItem, 'last_charge_fail');
  sel.shouldAutoUpgradeToTeams = _.partial(
    sel.dataItem,
    TEAMS_TRIAL_AUTO_UPGRADE
  );
  sel.pathsDepthLimit = _.partial(
    sel.dataItem,
    'limits.zap_branch_depth_limit'
  );
  sel.pathsWidthLimit = _.partial(sel.dataItem, 'limits.node_branch_limit');
  sel.allowImportExport = _.partial(sel.dataItem, 'plan.allow_import_export');
  sel.teamName = entity =>
    sel.isTeam(entity) ? sel.nameOrLoading(entity) : sel.ownerName(entity);

  sel.all.currentAccountId = _.partial(sel.all.stateItem, CURRENT_ACCOUNT_ID);
  sel.all.primaryAccountId = _.partial(sel.all.dataItem, 'primary_id');
  sel.all.currentAccount = state =>
    sel.all.entity(sel.all.currentAccountId(state), state);
  sel.all.currentAccountLimits = state =>
    sel.limits(sel.all.currentAccount(state));
  sel.all.currentPlanId = _.flow(sel.all.currentAccount, sel.planId);
  sel.all.currentPlanName = _.flow(sel.all.currentAccount, sel.planName);
  sel.all.currentPlanGroupId = _.flow(sel.all.currentAccount, sel.planGroupId);
  sel.all.isAccountIdValid = (accountId, state) => {
    // `_.compact` shouldn't be necessary here but prevents false positives
    // of `undefined` being in `allAccountIds` in case `accountId` is `undefined`
    const allAccountIds = _.compact(
      selectors.all.entities(state).map(selectors.id)
    );
    return _.includes(allAccountIds, accountId);
  };
  sel.all.hasAnyTeam = _.partial(sel.all.hasAnyBy, sel.isTeam);
  sel.all.hasMultiple = _.flow(
    sel.all.entities,
    entities => entities.length > 1
  );
  sel.all.hasAnyPaidBesides = (accountId, state) =>
    _.without(
      sel.all.filterBy(sel.isPaid, state),
      sel.all.entity(accountId, state)
    ).length > 0;
  sel.all.isLoading = state =>
    sel.all.stateItem('status', state) === entityStatuses.LOADING;
  sel.all.isLoggedIn = state =>
    !sel.all.isFailure(state) &&
    !sel.all.isLoading(state) &&
    sel.all.isSuccess(state);
  sel.all.isFailure = state =>
    sel.all.stateItem('status', state) === entityStatuses.FAILURE;
  sel.all.isSuccess = state =>
    sel.all.stateItem('status', state) === entityStatuses.SUCCESS;
  sel.all.shouldLoad = sel.all.isUnloaded;
  sel.all.logoChoices = partialWithDefault(
    sel.all.dataItem,
    ['logo_choices'],
    []
  );
  sel.all.findNewestAccount = state => {
    return _.maxBy(selectors.all.entities(state), selectors.id);
  };

  // We override `loadCollection()` to select the current account AFTER we're done loading.
  // This means once `hasLoaded()` is `true`, then `currentAccountId()` may still not be set.
  // Use this selector instead to wait for that to be done as well.
  sel.all.hasCurrentAccountInitialized = state =>
    sel.all.hasLoaded(state) &&
    (sel.all.isFailure(state) ||
      sel.all.isAccountIdValid(sel.all.currentAccountId(state), state));
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const overrideLoad = originalActionCreator => {
      return (...args) => (dispatch, getState) => {
        return dispatch(originalActionCreator(...args)).then(() => {
          // Whenever accounts have successfully loaded we need to reinitialize
          // the current account to ensure that it's valid.
          const state = getState();
          const currentAccountId = selectors.all.currentAccountId(state);
          const shouldInitializeCurrentAccount =
            selectors.all.isSuccess(state) &&
            !selectors.all.isAccountIdValid(currentAccountId, state);
          if (shouldInitializeCurrentAccount) {
            dispatch(entityActions.initalizeCurrentAccount());
          }
          if (isFullStoryInstalled()) {
            // If the account was initialized ealier the state will be updated
            // so ensure we have the most current state
            const fsState = getState();
            const switches = getSwitchesFromState(fsState);
            const currentAccount = selectors.all.currentAccount(fsState);
            const currentProfile = Profile.selectors.all.currentProfile(
              fsState
            );
            const roles = Profile.selectors.roles(currentProfile);
            let role = 'member';

            if (Array.isArray(roles)) {
              const accountId = selectors.all.currentAccountId(fsState);
              const currentAccountRole = roles.find(
                r => r.account_id === accountId
              );
              if (currentAccountRole) {
                role = currentAccountRole.role;
              }
            }

            setFullStoryUserVars({
              planId_str: selectors.planId(currentAccount),
              planName_str: selectors.planName(currentAccount),
              role_str: role,
              switches_str: JSON.stringify(switches),
            });
          }
        });
      };
    };

    entityActions.loadCollection = overrideLoad(entityActions.loadCollection);
    entityActions.forceLoadCollection = overrideLoad(
      entityActions.forceLoadCollection
    );

    entityActions.updateName = partialWithDefault(
      entityActions.updateEntityDataItem,
      [NAME]
    );

    entityActions.updateLogoChoice = partialWithDefault(
      entityActions.updateEntityDataItem,
      [LOGO_CHOICE]
    );

    entityActions.initalizeCurrentAccount = () =>
      getLocalStorage(
        [CURRENT_ACCOUNT_ID],
        (data, dispatch, getState) => {
          const state = getState();
          const storedCurrentAccountId = data[CURRENT_ACCOUNT_ID];
          const isStoredIdValid = selectors.all.isAccountIdValid(
            storedCurrentAccountId,
            state
          );
          const primaryAccountId = selectors.all.primaryAccountId(state);
          const currentAccountId = isStoredIdValid
            ? storedCurrentAccountId
            : primaryAccountId;
          dispatch(entityActions.setAndStoreCurrentAccountId(currentAccountId));
        },
        {
          readFromStorage: key => {
            try {
              // May be `NaN`; default to `undefined`.
              return parseInt(localStorage.getItem(key), 10) || undefined;
            } catch (e) {
              return undefined;
            }
          },
        }
      );

    entityActions.setAndStoreCurrentAccountId = id => dispatch => {
      dispatch(entityActions.setCurrentAccountId(id));
      dispatch(entityActions.storeCurrentAccountId(id));
    };

    entityActions.setCurrentAccountId = _.partial(
      entityActions.updateCollectionStateItem,
      CURRENT_ACCOUNT_ID
    );

    entityActions.storeCurrentAccountId = (id, onSet) =>
      setLocalStorage(
        {
          [CURRENT_ACCOUNT_ID]: id,
        },
        onSet,
        {
          writeToStorage: (key, value) => {
            try {
              localStorage.setItem(key, value);
            } catch (e) {
              // Ignore failures.
            }
          },
        }
      );

    entityActions.switchAccount = (id, message, delay = 1000) => dispatch => {
      dispatch(
        entityActions.storeCurrentAccountId(id, async () => {
          const switchingMessage = message || 'Switching accounts…';
          dispatch(ToastNotification.actions.createInfo(switchingMessage));

          // Switching accounts currently requires a page refresh to
          // easily clear out all of the current store state. In the
          // future we can clear out store state manually or use selectors
          // to filter data to the current account.
          setTimeout(() => {
            window.location = buildAccountSwitcherUrl(id);
          }, delay);
        })
      );
    };

    // Used to keep the current account up to date, especially for its limits.
    entityActions.reloadCurrentAccount = () => (dispatch, getState) =>
      dispatch(
        entityActions.loadEntity(
          null,
          selectors.all.currentAccountId(getState())
        )
      );

    entityActions.toggleAutoreplay = (shouldEnable, id) =>
      EntityActions.saveEntity(
        type,
        () => ({ [IS_AUTOREPLAY_ENABLED]: shouldEnable }),
        api.save,
        { shouldGlobalizeError: true },
        id
      );

    entityActions.transferOwnership = (
      messages,
      entityId,
      authenticationToken,
      formFields
    ) => {
      return EntityActions.saveEntity(
        type,
        () => ({
          ...formFields,
          auth_token: authenticationToken,
        }),
        api.save,
        messages,
        entityId
      );
    };

    entityActions.trialTeams = _.partial(
      entityActions.updateEntityDataItem,
      IS_TRIALING_TEAMS,
      true
    );

    entityActions.updateCompanyTrialExitSurvey = _.partial(
      entityActions.updateEntityDataItem,
      'surveys_submitted'
    );

    entityActions.toggleTeamsAutoUpgrade = _.partial(
      entityActions.updateEntityDataItem,
      TEAMS_TRIAL_AUTO_UPGRADE
    );

    entityActions.trialCompanies = _.partial(
      entityActions.updateEntityDataItem,
      IS_TRIALING_COMPANIES,
      true
    );

    entityActions.uploadImage = (file, messages, id) => {
      const defaultMessages = {
        success: 'Logo changed!',
        error: `
          Sorry, but we were unable to change your account logo to
          ${file.name || 'your file'}. Please try again.
        `,
        shouldNotify: true,
      };
      return EntityActions.saveEntity(
        type,
        () => ({ file }),
        api.uploadImage,
        { ...defaultMessages, ...(messages || {}) },
        id
      );
    };

    entityActions.updateTrialExtensionState = partialWithDefault(
      entityActions.updateEntityStateItem,
      [DID_EXTEND_TRIAL]
    );

    entityActions.updateCurrentTrialExtensionState = didExtendTrial => (
      dispatch,
      getState
    ) => {
      const currentAccountId = selectors.all.currentAccountId(getState());
      dispatch(
        entityActions.updateTrialExtensionState(
          didExtendTrial,
          currentAccountId
        )
      );
    };

    entityActions.extendCurrentTrial = (
      reason,
      shouldAutoUpgrade = false,
      messages
    ) => (dispatch, getState) => {
      const currentAccountId = selectors.all.currentAccountId(getState());
      const defaultMessages = {
        error: 'Trial could not be extended.',
        shouldNotify: true,
      };
      return dispatch(
        EntityActions.saveEntity(
          type,
          () => ({ reason, shouldAutoUpgrade }),
          api.extendTrial,
          { ...defaultMessages, ...(messages || {}) },
          currentAccountId
        )
      ).then(() => {
        const currentAccount = selectors.all.currentAccount(getState());
        const didExtendTrial =
          selectors.isSuccess(currentAccount) &&
          selectors.isTrialing(currentAccount) &&
          selectors.trialDaysLeft(currentAccount) > 0 &&
          selectors.hasExtendedTrialFor(reason, currentAccount);
        if (didExtendTrial) {
          dispatch(
            entityActions.updateTrialExtensionState(
              true,
              selectors.id(currentAccount)
            )
          );
        }
      });
    };
  }
);
